<template>
  <SlotModal :display="modelValue" @close="closeModal">
    <template v-if="canBeDeleted">
      <section v-if="state === STATE.DELETE_CONFIRM" id="delete-confirm-section">
        <div id="header" class="font-heading font-semibold text-xl mb-2">Please Confirm</div>
        <div id="message" class="font-sans text-base">
          Are you sure you want to delete the <b>{{ showName }}</b> Performance?
        </div>
        <ErrorDisplay v-bind="ui.error" />
        <div class="flex justify-between mt-8">
          <ButtonDefault label="Cancel" id="cancel-btn" @click="closeModal" alt />
          <ButtonDefault label="Delete" id="delete-btn" @click="onPerformanceDelete" altWarning />
        </div>
      </section>
      <section v-else-if="state === STATE.DELETING" id="deleting-section">
        <div class="flex flex-col">
          <div class="mb-8 text-center" id="header">Deleting...</div>
          <LoaderLine />
        </div>
      </section>
    </template>
    <section v-else id="delete-not-allowed-section">
      <div id="header" class="font-heading font-semibold text-xl mb-2">Delete Not Allowed</div>
      <div id="message" class="font-sans text-base">
        The <span class="font-semibold">{{ showName }}</span> performance has active reservations
        and cannot be deleted at this time.
      </div>
      <div class="flex justify-between mt-8">
        <ButtonDefault label="Cancel" id="cancel-btn" @click="closeModal" alt />
      </div>
    </section>
  </SlotModal>
</template>

<script setup lang="ts">
import { computed, ref, toRefs } from 'vue'
import { useRouter } from 'vue-router'

import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import ErrorDisplay from '@/common/components/atoms/ErrorDisplay.vue'
import LoaderLine from '@/common/components/atoms/LoaderLine.vue'
import SlotModal from '@/common/components/organisms/SlotModal.vue'
import { usePerformanceStore } from '@/modules/performance/stores/PerformanceStore'
import { blankError, displayHandled } from '@/utils/errors'

import { PerformanceRead, ShowId } from '@generated/types'

const STATE = {
  DELETE_CONFIRM: 'delete-confirm',
  DELETING: 'deleting',
}

const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (event: 'update:modelValue', value: boolean): void
}>()
const props = withDefaults(
  defineProps<{
    modelValue: boolean
    performance: PerformanceRead
    canBeDeleted?: boolean
  }>(),
  {
    canBeDeleted: true,
  },
)
const { performance, canBeDeleted, modelValue } = toRefs(props)

const state = ref<string>(STATE.DELETE_CONFIRM)
const showName = computed<string>(() => performance.value.show.name)

const performanceStore = usePerformanceStore()
const ui = ref({ error: blankError() })
async function onPerformanceDelete() {
  state.value = STATE.DELETING
  try {
    const showId = performance.value.show.id
    await performanceStore.deletePerformance(performance.value.id)
    await redirectToShowDetail(showId)
  } catch (error) {
    state.value = STATE.DELETE_CONFIRM
    displayHandled(error, ui.value.error)
  }
}
const router = useRouter()
async function redirectToShowDetail(showId: ShowId) {
  await router.push({
    name: 'showDetail',
    params: { id: showId },
  })
}

function closeModal() {
  if (state.value === STATE.DELETING) return
  state.value = STATE.DELETE_CONFIRM
  emit('update:modelValue', false)
}
</script>
